import { createMRTColumnHelper, MRT_PaginationState, MRT_SortingState } from "material-react-table";
import { useTranslation } from "react-i18next";
import { FoodsIcon, Typography } from "@foodpilot/foods";
import { ProductFormProductIngredient } from "src/pages/Product/ProductForm/ProductForm.types";
import { Link } from "react-router";
import { useTheme } from "@mui/material";
import { URLHandler } from "src/services/URLHandler";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { ListGrid } from "src/pages/Product/ProductForm/ListGrid";
import { useState, useMemo } from "react";
import { mrtSortingStateToObject } from "src/api/apiHelpers";
import { useGetUnifiedIngredientList } from "src/api/client-api/ingredients/useGetUnifiedIngredientList";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { useGetAllIngredientReferencesUnified } from "src/api/client-api/ingredientReferenceUnified/useGetAllIngredientReferencesUnified";

export type IngredientGridProps = {
    delayedSearch: string;
    existingIngredient: ProductFormProductIngredient[];
    setNewIngredients: (ingredients: ProductFormProductIngredient[]) => void;
};
// Type for unified list (ingredients + intermediate products)
type UnifiedEntity = {
    "@id": string;
    id: number;
    name: string;
    type: "ingredient" | "intermediate-product" | "ingredient-reference";
    clientId?: string | null;
    origin?: string | null;
    ingredientLine?: { name: string } | null;
    compositeId?: string;
    source?: "sure" | "foodpilot";
    defaultOrigin?: string | null;
    fresh?: boolean | null;
    nonEdiblePartRatio?: number | null;
    rawToCookedRatio?: number | null;
};

export function IngredientListGrid(props: IngredientGridProps) {
    const { setNewIngredients, existingIngredient, delayedSearch } = props;
    const { selectedCompany } = useFoodpilotContext();
    const { t } = useTranslation();
    const theme = useTheme();
    const { isFeatureActivated } = useApplicationParameters();
    const areIngredientsEnabled = isFeatureActivated("FS-entity-ingredients");
    const areManagedIngredientsEnabled = isFeatureActivated("FS-manage-my-ingredients");
    // Level 2 (manage my ingredients): pick platform Ingredient IRIs. Otherwise: Sure/Foodpilot references.
    const shouldUseManagedIngredients = areIngredientsEnabled && areManagedIngredientsEnabled;

    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 6,
    });
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "name", desc: false }]);

    // Query unified ingredient list (server-side pagination and sorting)
    const { unifiedIngredients, isLoading, totalItems } = useGetUnifiedIngredientList({
        cacheTime: 0,
        enabled: shouldUseManagedIngredients,
        params: {
            page: pagination.pageIndex + 1,
            itemsPerPage: pagination.pageSize,
            organization_id: selectedCompany?.id,
            ...mrtSortingStateToObject(sorting),
        },
        search: delayedSearch,
    });
    const {
        ingredientReferences,
        isLoading: areIngredientReferencesLoading,
        totalItems: totalIngredientReferences,
    } = useGetAllIngredientReferencesUnified({
        q: delayedSearch,
        source: "all",
        page: pagination.pageIndex + 1,
        itemsPerPage: pagination.pageSize,
        enabled: areIngredientsEnabled && !shouldUseManagedIngredients,
    });

    // Map to UnifiedEntity format
    const unifiedData: UnifiedEntity[] = useMemo(() => {
        if (!shouldUseManagedIngredients) {
            return (ingredientReferences ?? []).map((item) => ({
                "@id": item.compositeId,
                id: item.id,
                name: item.name,
                type: "ingredient-reference",
                origin: item.defaultOrigin,
                compositeId: item.compositeId,
                source: item.source,
                defaultOrigin: item.defaultOrigin,
                fresh: item.fresh,
                nonEdiblePartRatio: item.nonEdiblePartRatio,
                rawToCookedRatio: item.rawToCookedRatio,
            }));
        }

        return (unifiedIngredients ?? []).map((item) => ({
            "@id": item["@id"],
            id: item.id,
            name: item.name,
            type: item.type,
            clientId: item.clientId,
            origin: item.origin,
            ingredientLine: item.ingredientLine,
        }));
    }, [shouldUseManagedIngredients, ingredientReferences, unifiedIngredients]);

    const selectionHandler = (entity: ProductFormProductIngredient) => {
        if (entity.ingredient) {
            return entity.ingredient["@id"];
        }
        if (entity.intermediateProduct) {
            return entity.intermediateProduct["@id"];
        }
        if (entity.ingredientReference) {
            return entity.ingredientReference.compositeId;
        }
        return "";
    };

    const newEntityHandler = (entity: UnifiedEntity) => {
        if (entity.type === "ingredient") {
            const newIngredient: ProductFormProductIngredient = {
                ingredient: {
                    "@id": entity["@id"],
                    id: entity.id,
                    name: entity.name,
                    origin: entity.origin ?? null,
                    suppliersName: [],
                },
                quantity: null,
                usedQuantity: null,
            };
            return newIngredient;
        } else if (entity.type === "intermediate-product") {
            const newIntermediateProduct: ProductFormProductIngredient = {
                intermediateProduct: {
                    "@id": entity["@id"],
                    id: entity.id,
                    title: entity.name,
                },
                quantity: null,
                usedQuantity: null,
            };
            return newIntermediateProduct;
        }

        if (!entity.compositeId || !entity.source) {
            throw new Error("Ingredient reference entity must include a composite id and a source");
        }

        const newIngredientReference: ProductFormProductIngredient = {
            ingredientReference: {
                compositeId: entity.compositeId,
                id: entity.id,
                source: entity.source,
                name: entity.name,
                defaultOrigin: entity.defaultOrigin,
                fresh: entity.fresh,
                nonEdiblePartRatio: entity.nonEdiblePartRatio,
                rawToCookedRatio: entity.rawToCookedRatio,
            },
            quantity: null,
            usedQuantity: null,
            origin: entity.defaultOrigin ?? null,
        };
        return newIngredientReference;
    };

    const columnHelper = createMRTColumnHelper<UnifiedEntity>();
    const noValue = "-";
    const columns = [
        columnHelper.accessor("name", {
            id: "name",
            header: t("nom_de_l_ingredient"),
            Cell: (value) => {
                const companyId = selectedCompany?.id;
                const entity = value.row.original;
                const entityId = entity.id;
                const entityType = entity.type;

                const url =
                    companyId === undefined ? undefined
                    : entityType === "ingredient" ? URLHandler.ingredient.scores(companyId, entityId)
                    : entityType === "intermediate-product" ? URLHandler.intermediateProduct.scores(companyId, entityId)
                    : undefined;

                return (
                    <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                        {entityType === "intermediate-product" && <FoodsIcon icon="products" size={2} />}
                        {url ?
                            <Link
                                to={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: theme.palette.primary[700],
                                    textDecoration: "none",
                                    display: "flex",
                                    justifyContent: "left",
                                    gap: "5px",
                                    alignItems: "center",
                                }}
                            >
                                {value.renderedCellValue}
                                <FoodsIcon icon="openNewTab" size={2} />
                            </Link>
                        :   <Typography variant="body" sx={{ color: "#000" }}>
                                {value.renderedCellValue}
                            </Typography>
                        }
                    </div>
                );
            },
        }),
        columnHelper.accessor((row) => row.clientId ?? noValue, {
            id: "clientId",
            header: t("unique_identifier"),
            Cell: (value) => (
                <Typography variant="body" sx={{ color: "#000" }}>
                    {value.renderedCellValue}
                </Typography>
            ),
        }),
        columnHelper.accessor((row) => row.ingredientLine?.name ?? noValue, {
            id: "ingredientLine.name",
            header: t("filiere"),
            Cell: (value) => (
                <Typography variant="body" sx={{ color: "#000" }}>
                    {value.renderedCellValue}
                </Typography>
            ),
        }),
    ];

    return (
        <ListGrid
            data={unifiedData}
            columns={columns}
            delayedSearch={delayedSearch}
            existingEntities={existingIngredient}
            setNewEntities={setNewIngredients}
            selectionHandler={selectionHandler}
            newEntityHandler={newEntityHandler}
            isLoading={shouldUseManagedIngredients ? isLoading : areIngredientReferencesLoading}
            totalItems={(shouldUseManagedIngredients ? totalItems : totalIngredientReferences) ?? 0}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
        />
    );
}
